"use client";

import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import SettingsRoundedIcon from "@mui/icons-material/SettingsRounded";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import Toolbar from "@mui/material/Toolbar";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useState } from "react";
import * as React from "react";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../services/AuthService";
import { setAuthUser } from "../../store/slices/authUser";
import { colors } from "../../theme/colors";
import CompactMarketplaceSelector from "./CompactMarketplaceSelector";
import HeaderLanguageControl from "./HeaderLanguageControl";
const theme = createTheme({
  typography: {
    fontFamily: '"Roboto","Helvetica","Arial",sans-serif'
  },
  palette: {
    primary: {
      main: colors.primary.main
    }
  }
});
function Header() {
  const router = useRouter();
  const pathname = usePathname();
  const h = useTranslations("header");
  const dispatch = useDispatch();
  const authUser = useSelector(state => state.auth.authUser);
  const shouldShowHeader = pathname === "/login";

  // Avatar menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const menuOpen = Boolean(anchorEl);
  const handleAvatarClick = event => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };
  const handleLogout = async () => {
    handleMenuClose();
    try {
      await logout();
    } catch {}
    localStorage.removeItem("token");
    dispatch(setAuthUser({}));
    router.push("/");
  };

  // User initials for avatar
  const userName = authUser?.first_name || authUser?.name || authUser?.email || "";
  const userEmail = authUser?.email || "";
  const initials = userName ? userName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2) : "?";
  if (shouldShowHeader) {
    return null;
  }
  return <ThemeProvider theme={theme} data-sentry-element="ThemeProvider" data-sentry-component="Header" data-sentry-source-file="Header.jsx">
      <AppBar position="fixed" sx={{
      bgcolor: "white",
      boxShadow: `0 1px 3px ${colors.border.light}`,
      height: "72px",
      display: "flex",
      justifyContent: "center"
    }} data-sentry-element="AppBar" data-sentry-source-file="Header.jsx">
        <Container maxWidth="auto" data-sentry-element="Container" data-sentry-source-file="Header.jsx">
          <Toolbar disableGutters sx={{
          justifyContent: "space-between",
          alignItems: "center",
          height: "72px",
          px: {
            xs: 2,
            md: 3
          }
        }} data-sentry-element="Toolbar" data-sentry-source-file="Header.jsx">
            {/* Logo - Desktop Only */}
            <Box sx={{
            display: {
              xs: "none",
              md: "flex"
            },
            alignItems: "center",
            height: "70px",
            width: "70px"
          }} data-sentry-element="Box" data-sentry-source-file="Header.jsx">
              <Link href="/dashboard" data-sentry-element="Link" data-sentry-source-file="Header.jsx">
                <Image src="/assets/svg/login/genslogo.webp" height={70} width={70} alt="logo" priority style={{
                cursor: 'pointer'
              }} data-sentry-element="Image" data-sentry-source-file="Header.jsx" />
              </Link>
            </Box>

            {/* Mobile Left Spacer */}
            <Box sx={{
            display: {
              xs: "flex",
              md: "none"
            },
            width: "60px",
            // Space for burger menu
            height: "48px"
          }} data-sentry-element="Box" data-sentry-source-file="Header.jsx" />

            {/* Right Side Controls */}
            <Box sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5
          }} data-sentry-element="Box" data-sentry-source-file="Header.jsx">
              {/* Compact Marketplace Selector */}
              <CompactMarketplaceSelector data-sentry-element="CompactMarketplaceSelector" data-sentry-source-file="Header.jsx" />

              {/* Language Control */}
              <HeaderLanguageControl data-sentry-element="HeaderLanguageControl" data-sentry-source-file="Header.jsx" />

              {/* User Avatar Menu */}
              <Tooltip title={h("AccountMenu")} placement="bottom" data-sentry-element="Tooltip" data-sentry-source-file="Header.jsx">
                <Avatar onClick={handleAvatarClick} sx={{
                width: 34,
                height: 34,
                bgcolor: colors.primary.main,
                color: "white",
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "box-shadow 150ms ease-out, transform 150ms ease-out",
                "&:hover": {
                  boxShadow: `0 0 0 3px ${colors.border.light}`,
                  transform: "scale(1.05)"
                },
                "&:active": {
                  transform: "scale(0.97)"
                }
              }} data-sentry-element="Avatar" data-sentry-source-file="Header.jsx">
                  {initials}
                </Avatar>
              </Tooltip>

              <Menu anchorEl={anchorEl} open={menuOpen} onClose={handleMenuClose} onClick={handleMenuClose} slotProps={{
              paper: {
                elevation: 3,
                sx: {
                  mt: 1,
                  minWidth: 200,
                  borderRadius: "10px",
                  border: `1px solid ${colors.border.light}`,
                  "& .MuiMenuItem-root": {
                    fontSize: "14px",
                    py: 1,
                    px: 2,
                    borderRadius: "6px",
                    mx: 0.75,
                    my: 0.25,
                    transition: "background-color 120ms ease-out"
                  }
                }
              }
            }} transformOrigin={{
              horizontal: "right",
              vertical: "top"
            }} anchorOrigin={{
              horizontal: "right",
              vertical: "bottom"
            }} data-sentry-element="Menu" data-sentry-source-file="Header.jsx">
                {/* User info header */}
                <Box sx={{
                px: 2,
                py: 1.5,
                pb: 1
              }} data-sentry-element="Box" data-sentry-source-file="Header.jsx">
                  <Typography sx={{
                  fontSize: "14px",
                  fontWeight: 600,
                  color: colors.text.primary,
                  lineHeight: 1.3
                }} data-sentry-element="Typography" data-sentry-source-file="Header.jsx">
                    {userName}
                  </Typography>
                  {userEmail && <Typography sx={{
                  fontSize: "12px",
                  color: colors.text.tertiary,
                  mt: 0.25
                }}>
                      {userEmail}
                    </Typography>}
                </Box>

                <Divider sx={{
                my: 0.5
              }} data-sentry-element="Divider" data-sentry-source-file="Header.jsx" />

                {/* Settings */}
                <MenuItem onClick={() => router.push("/settings")} data-sentry-element="MenuItem" data-sentry-source-file="Header.jsx">
                  <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="Header.jsx">
                    <SettingsRoundedIcon sx={{
                    fontSize: 18,
                    color: colors.text.tertiary
                  }} data-sentry-element="SettingsRoundedIcon" data-sentry-source-file="Header.jsx" />
                  </ListItemIcon>
                  {h("Settings")}
                </MenuItem>

                <Divider sx={{
                my: 0.5
              }} data-sentry-element="Divider" data-sentry-source-file="Header.jsx" />

                {/* Logout */}
                <MenuItem onClick={handleLogout} data-sentry-element="MenuItem" data-sentry-source-file="Header.jsx">
                  <ListItemIcon data-sentry-element="ListItemIcon" data-sentry-source-file="Header.jsx">
                    <LogoutRoundedIcon sx={{
                    fontSize: 18,
                    color: colors.text.tertiary
                  }} data-sentry-element="LogoutRoundedIcon" data-sentry-source-file="Header.jsx" />
                  </ListItemIcon>
                  {h("Logout")}
                </MenuItem>
              </Menu>

            </Box>

          </Toolbar>
        </Container>
      </AppBar>
    </ThemeProvider>;
}
export default Header;